/* eslint-disable no-nested-ternary */
import React, {
  useState, useEffect, useMemo, useCallback,
} from 'react';
import {
  Modal, Input, Loader, Segment, Button, Icon, Image,
} from 'semantic-ui-react';
import useTheme from 'theme/useTheme';
import { UserChip } from 'components/lib/Chips';
import { toast } from 'react-hot-toast';
import IdeasImage from 'src/images/ideas.png';
import { ThemedButton } from 'components/lib/UI';
import util from 'utils/utils';
import api from 'api';
import { OpenAPI } from 'simplydo/interfaces';
import ConfigurableTable from 'components/lib/ConfigurableTable';

const defaultEnabledFeatures: {
  search?: boolean;
  invite?: boolean;
} = {
  search: true,
  invite: true,
};

type UserChooserProps = {
  trigger?: React.ReactElement;
  instructions?: string;
  searchFunction: (search: string, callback: (users: OpenAPI.Schemas['User'][]) => void) => void;
  confirm?: string;
  onComplete: (users: Partial<OpenAPI.Schemas['User']>[], onCompleteItems?: any) => void;
  onCompleteItems?: any;
  forType: 'assessor' | 'collaborator' | 'group' | 'projectInvited' | 'projectAssignee';
  forId: string;
  enabledFeatures?: typeof defaultEnabledFeatures;
  controlled?: boolean;
  isOpen?: boolean;
  onClose?: () => void;
  clearOnComplete?: boolean;
  message?: React.ReactElement;
  externalInvitesInAudience?: boolean;
  audienceWarningText?: React.ReactElement;
  single?: boolean;
}

const SearchInfoText = 'Search for an existing user';
const InviteInfoText = 'Invite a new user using their email address';

const SearchPlaceholderText = 'Enter a search term';
const InvitePlaceholderText = 'Enter an email address to invite';

const UserChooser = ({
  trigger, instructions, searchFunction, confirm, onComplete, forType, forId, enabledFeatures = defaultEnabledFeatures, controlled, isOpen, onClose, onCompleteItems, clearOnComplete, message, externalInvitesInAudience = true, audienceWarningText, single,
}: UserChooserProps) => {
  const [potentialUsers, setPotentialUsers] = useState([]);
  const [potentialUserSearch, setPotentialUserSearch] = useState('');
  const [chosenUsers, setChosenUsers] = useState([]);
  const [loading] = useState(false);
  const [open, setOpen] = useState(false);
  const theme = useTheme();

  useEffect(() => {
    if (!open && !isOpen) {
      if (potentialUserSearch !== '') {
        setPotentialUserSearch('');
        setPotentialUsers([]);
      }
      return;
    }

    if (!potentialUserSearch) {
      setPotentialUsers([]);
    } else {
      searchFunction(potentialUserSearch, (users) => {
        setPotentialUsers(users);
      });
    }
  }, [potentialUserSearch, searchFunction, isOpen, open]);

  useEffect(() => {
    if (controlled) {
      setOpen(!!isOpen);
    }
  }, [controlled, isOpen]);

  const handleClose = useCallback(() => {
    setOpen(false);
    onClose && onClose();
  }, [onClose]);

  const pickUser = useCallback((u) => {
    if (single) {
      setChosenUsers([u]);
    } else {
      if (chosenUsers.filter((cu) => cu._id === u._id).length) return;
      const newChosenUsers = Object.assign([], chosenUsers);
      newChosenUsers.push(u);
      setChosenUsers(newChosenUsers);
    }
  }, [chosenUsers, single]);

  const unPickUser = (userId) => {
    const newChosenUsers = Object.assign([], chosenUsers.filter((u) => u._id !== userId));
    setChosenUsers(newChosenUsers);
  };
  const finish = () => {
    onComplete(chosenUsers, onCompleteItems);
    if (controlled) onClose();
    else setOpen(false);
    if (clearOnComplete) {
      setPotentialUserSearch('');
      setPotentialUsers([]);
      setChosenUsers([]);
    }
  };

  const inviteUser = useCallback(() => {
    // If search is not allowed we should always use the email directly rather than resolve the account
    if (enabledFeatures.search) {
      // check if a user with the email holds an account
      api.users.findByEmail({ email: potentialUserSearch, forType, forId }, (data) => {
        if (data?.user) {
          pickUser(data.user);
        } else {
          // Validate email entered
          if (!util.validateEmail(potentialUserSearch)) {
            toast.error('Please enter a valid email address');
            return;
          }
          setChosenUsers([...chosenUsers, {
            isEmailInvitee: true, _id: potentialUserSearch, profile: { fullName: potentialUserSearch }, isInAudience: externalInvitesInAudience,
          }]);
        }
        setPotentialUserSearch('');
      }, () => {});
    } else {
      // Validate email entered
      if (!util.validateEmail(potentialUserSearch)) {
        toast.error('Please enter a valid email address');
        return;
      }
      setChosenUsers([...chosenUsers, {
        isEmailInvitee: true, _id: potentialUserSearch, profile: { fullName: potentialUserSearch }, isInAudience: externalInvitesInAudience,
      }]);
    }
  }, [enabledFeatures.search, potentialUserSearch, forType, forId, pickUser, chosenUsers, externalInvitesInAudience]);

  const shouldShowEmailInvitationButton = useMemo(() => (
    (
      util.validateEmail(potentialUserSearch)
      && !potentialUsers?.length
      && enabledFeatures.invite
    ) || (
      enabledFeatures.invite
      && !enabledFeatures.search
    )
  ), [enabledFeatures.invite, enabledFeatures.search, potentialUserSearch, potentialUsers?.length]);

  const chosenIds = useMemo(() => chosenUsers.map((c) => c._id), [chosenUsers]);
  const potentialIds = useMemo(() => potentialUsers.map((c) => c._id), [potentialUsers]);
  const filteredChosenUsers = useMemo(() => chosenUsers.filter((p) => !potentialIds.includes(p._id)), [potentialIds, chosenUsers]);
  const cannotViewChosenUsers = useMemo(() => chosenUsers.filter((c) => !c?.isInAudience), [chosenUsers]);

  return (
    <>
      {!controlled
        && (
          trigger
            ? React.cloneElement(trigger, { onClick: () => setOpen(!open) })
            : <Button size='small' primary icon='lightbulb' content='Choose a challenge' onClick={() => setOpen(!open)} />
        )
      }

      <Modal open={open} dimmer='inverted' size='tiny' onClose={handleClose} style={theme.sizes.isMobile ? { maxWidth: '95%', left: 5 } : null} data-testid='simplydo-user-chooser'>
        <Modal.Header>
          <h3 style={{ margin: 0 }}>Search for an existing user, or invite a new user</h3>
          {instructions && <h5 style={{ marginTop: 0 }}>{instructions}</h5>}
        </Modal.Header>
        {message
          ? (
            <Modal.Content>
              {message}
            </Modal.Content>
          ) : null
        }
        <Modal.Content>
          {loading && <Loader active />}
          <p>
            {enabledFeatures.search && enabledFeatures.invite
              ? `${SearchInfoText}, or ${InviteInfoText.toLowerCase()}`
              : enabledFeatures.search
                ? SearchInfoText
                : InviteInfoText
            }
          </p>
          <Input
            placeholder={
              enabledFeatures.search && enabledFeatures.invite
                ? `${SearchPlaceholderText}, or ${InvitePlaceholderText.toLowerCase()}`
                : enabledFeatures.search
                  ? SearchPlaceholderText
                  : InvitePlaceholderText
            }
            fluid value={potentialUserSearch}
            onChange={(e) => setPotentialUserSearch(e.target.value)}
            action={
              shouldShowEmailInvitationButton
                ? {
                  content: 'Add invitation',
                  onClick: inviteUser,
                  style: { backgroundColor: theme.secondaryColour, color: util.getTextColour(theme.secondaryColour) },
                }
                : null
            }
            style={{ marginBottom: 5 }}
          />
          {!loading && !chosenUsers?.length && !potentialUsers?.length
            ? (
              <Segment textAlign='center'>
                <img src={IdeasImage} style={{
                  maxHeight: 100, maxWidth: '80%', display: 'block', margin: '5px auto',
                }} alt='No users chosen yet' />
                <h4>No users chosen yet</h4>
              </Segment>
            ) : (
              <>
                <ConfigurableTable
                  restrictOverflow
                  preventSelectAll={single}
                  selectedKeys={chosenIds}
                  tableKey='userChooser'
                  data={filteredChosenUsers.concat(potentialUsers)}
                  onSelect={(item, selected) => {
                    if (!item) {
                      if (selected) {
                        setChosenUsers(filteredChosenUsers.concat(potentialUsers));
                      } else {
                        setChosenUsers([]);
                      }
                      return;
                    }
                    if (selected) {
                      pickUser(item);
                    } else {
                      unPickUser(item._id);
                    }
                  }}
                  columns={[
                    {
                      key: 'avatar',
                      name: '',
                      settingName: 'Avatar',
                      center: true,
                      width: 20,
                      render: ({ item }) => (item.isEmailInvitee
                        ? <Icon
                            name='mail'
                            size='large'
                            color='grey'
                          />
                        : <Image
                            style={{ objectFit: 'cover' }}
                            avatar
                            src={util.avatarUrl(item)}
                          />),
                      sortable: true,
                    },
                    {
                      key: 'fullName',
                      name: 'Name',
                      render: ({ item }) => item.profile.fullName,
                      sortable: true,
                    },
                    {
                      key: 'jobTitle',
                      name: 'Job Title',
                      render: ({ item }) => item.profile.jobTitle,
                      sortable: true,
                    },
                    {
                      key: 'department',
                      name: 'Department',
                      render: ({ item }) => item.profile.department,
                      sortable: true,
                    },
                  ]}
                />
              </>
            )}
          {chosenUsers?.length > 0
            && <>
              {audienceWarningText && cannotViewChosenUsers?.length > 0
                && <>
                  <h5>
                    <Icon name='exclamation triangle' color='orange' />
                    {audienceWarningText}
                  </h5>
                  {cannotViewChosenUsers.map((c) => <div style={{ display: 'inline-block', margin: 10 }}>
                    <UserChip openInTab disableLink={c.isEmailInvitee} user={c} />
                    <Button size='mini' icon='trash' basic style={{ marginLeft: 5 }} onClick={() => unPickUser(c._id)} />
                  </div>)}
                </>
              }
            </>
          }
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setOpen(false)} content='Cancel' />
          <ThemedButton disabled={chosenUsers?.length === 0} primary content={confirm || `Continue with ${util.pluralise(chosenUsers.length, 'user', 'users')}`} onClick={finish} />
        </Modal.Actions>
      </Modal>
    </>
  );
};

export default UserChooser;
